import * as React from "react";
import { useEffect, useState } from "react";
import { WorkflowNode, NodeId, getNodeIcon } from "../types";
import { EndNode, Connector, Node } from "./CanvasHelpers";
import { OptionNodes } from "./OptionNodes";
import { ConnectorData, DraggingData } from "../WorkflowsEditor";
import {
  getNodeOptions,
  getNodeColors,
  getNodeTitle,
  getNodeText
} from "../WorkflowNodeHelpers";
import useAIConfigs from "@/hooks/useAIConfigs";
import LoadingSpinner from "@/components/LoadingSpinner";
import useTeams from "@/hooks/useTeams";
import useUsers from "@/hooks/useUsers";
import useWorkflows from "@/hooks/useWorkflows";
import { channelDefs, Channel } from "@/types";

interface Props {
  nodes: Record<NodeId, WorkflowNode>;
  scale: number;
  draggingData: DraggingData | null;
  selectedNodeId: NodeId | null;
  selectedBranchIndex: number | null;
  selectedConnector?: HTMLDivElement;
  channelPerspective?: string | null;
  onLoad: () => void;
  onSelectNode: (id: NodeId) => void;
  onSelectBranch: (id: NodeId, branchIndex: number) => void;
  onSelectConnector: (data: ConnectorData) => void;
  onDeleteNode: (id: NodeId) => void;
  onCopyNode: (id: NodeId) => void;
  onDragStart: (id: NodeId, event: React.DragEvent<HTMLDivElement>) => void;
  onDragOverConnector: (data: ConnectorData) => void;
  onDragLeaveConnector: () => void;
  onDragEnd: (id: NodeId) => void;
}

export default function WorkflowsCanvas({
  nodes,
  scale,
  draggingData,
  selectedNodeId,
  selectedBranchIndex,
  selectedConnector,
  channelPerspective,
  onLoad,
  onSelectNode,
  onSelectBranch,
  onSelectConnector,
  onDeleteNode,
  onCopyNode,
  onDragStart,
  onDragOverConnector,
  onDragLeaveConnector,
  onDragEnd
}: Props) {
  const [loaded, setLoaded] = useState(false);

  const { aiConfigurations, loading } = useAIConfigs();
  const { getDisplayNameForUser } = useUsers();
  const { getTeamNameById } = useTeams();
  const { workflows } = useWorkflows();

  useEffect(() => {
    if (loaded) {
      return;
    }
    //using requestAnimationFrame to ensure nodes are rendered before calculating position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        onLoad();
      });
    });
    setLoaded(true);
  }, [loaded, onLoad]);

  function renderRootNode() {
    const root = nodes[1];
    if (!root) return <EndNode />;
    return renderNode(root);
  }

  function renderChildOrEnd(child: NodeId | undefined) {
    child = child == undefined ? -1 : child;
    const node = nodes[child];
    if (!node) {
      return <EndNode />;
    }
    return renderNode(node);
  }

  // AI action types that support channel overrides
  const AI_ACTION_TYPES = new Set([
    "transferToAI", "netsuiteAI", "dynamicsAI", "hubSpotAI", "bigCommerceAI", "sapAI"
  ]);

  function renderNode(node: WorkflowNode): React.ReactNode {
    const NodeIcon = getNodeIcon(node);

    const options = getNodeOptions(node);
    const hasOptions = !!options && options.length > 0;
    const nodeColors = getNodeColors(node);

    // Show channel badge on AI nodes when viewing from a channel perspective
    const isAiNode = AI_ACTION_TYPES.has(node.actionType);
    const badge = channelPerspective && isAiNode
      ? channelDefs[channelPerspective as Channel] ?? null
      : undefined;

    return (
      <>
        <Node
          selected={selectedNodeId === node.id}
          type={node.type}
          action={node.actionType}
          icon={<NodeIcon className="text-white" />}
          title={getNodeTitle(node)}
          text={getNodeText(
            node,
            nodes,
            aiConfigurations,
            getDisplayNameForUser,
            getTeamNameById,
            workflows
          )}
          name={node.name}
          hasOptions={hasOptions}
          nodeColors={nodeColors}
          channelBadge={badge ?? undefined}
          nodeId={node.id}
          onSelect={() => onSelectNode(node.id)}
          onCopy={() => onCopyNode(node.id)}
          onDelete={() => onDeleteNode(node.id)}
          onDragStart={(e) => onDragStart(node.id, e)}
          onDragEnd={() => onDragEnd(node.id)}
        />
        {hasOptions && (
          <OptionNodes
            scale={scale}
            nodeId={node.id}
            options={options}
            color={nodeColors.optionColor}
            actionType={node.actionType}
            selectedConnector={selectedConnector}
            selectedBranchIndex={selectedNodeId === node.id ? selectedBranchIndex : null}
            draggingData={draggingData}
            onSelectNode={onSelectNode}
            onSelectBranch={onSelectBranch}
            renderChildOrEnd={renderChildOrEnd}
            onSelectConnector={onSelectConnector}
            onDragOverConnector={onDragOverConnector}
            onDragLeaveConnector={onDragLeaveConnector}
          />
        )}
        {!hasOptions &&
          node.actionType !== "jumpTo" &&
          node.actionType !== "condition" && (
            <>
              <Connector
                draggingData={draggingData}
                selectedConnector={selectedConnector}
                onClick={(el) =>
                  onSelectConnector({
                    el,
                    parentNodeId: node.id,
                    childNodeId: node.child
                  })
                }
                onDragEnter={(el) =>
                  onDragOverConnector({
                    el,
                    parentNodeId: node.id,
                    childNodeId: node.child
                  })
                }
                onDragLeave={onDragLeaveConnector}
              />
              {renderChildOrEnd(node.child)}
            </>
          )}
      </>
    );
  }

  const channelLabel = channelPerspective
    ? channelDefs[channelPerspective as Channel] ?? channelPerspective
    : null;

  return (
    <div
      id="canvas"
      className={`flex w-[5000px] h-[5000px] items-start justify-center`}
    >
      <div className="relative flex flex-col items-center justify-center">
        {channelLabel && (
          <div className="mb-4 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700 flex items-center gap-2">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 16v-4M12 8h.01" />
            </svg>
            <span>
              Viewing from <strong>{channelLabel}</strong> perspective — AI nodes with channel-specific overrides are highlighted
            </span>
          </div>
        )}
        {loading && (
          <div className="h-full">
            <LoadingSpinner />
          </div>
        )}
        {!loading && renderRootNode()}
      </div>
    </div>
  );
}
